import React, { useEffect, useState, useMemo } from 'react';
import { Button, Form, List, Modal } from 'semantic-ui-react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { AllocationAndStrategy, Availability } from '../../api/services/next';
import { useTextResources, useMessageResources } from '../../language';
import { onError, onWarning, onSuccess } from '../shared/messages';
import { DatePicker } from '../shared/DatePicker';
import Weekdays from '../shared/Weekdays';
import WeekDaysSelect from '../shared/WeekDaysSelect';
import { dateFormat } from '../shared/constants';
import NumericInput from '../shared/NumericInput';
import { handleStartEndChange, checkForCollisionInDateRanges, getIntWeekdays, filterDateRangesByDateRange } from '../shared/utils';
import Panel from '../shared/Panel';
import { RecalculateAvailabilityBySpecification } from '../../api/services/pms';
import { TrashButton } from '../shared/icons';
import { useLoader } from '../../hooks/useLoader';
import { useConfirm } from '../../hooks/useConfirm';

const initialState = {
    startDate: moment().startOf("day").toDate(),
    endDate: moment().add(1, "day").toDate(),
    weekdays: 0
};

export const AllocationDateRangesPopup = ({ open, selectedAllocation, onClose }) => {
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();
    const confirm = useConfirm();

    const [fromFilterDate, setFromFilterDate] = useState();
    const [toFilterDate, setToFilterDate] = useState();

    const [allocationDateRanges, setAllocationDateRanges] = useState([]);
    const [startDate, setStartDate] = useState(initialState.startDate);
    const [endDate, setEndDate] = useState(initialState.endDate);
    const [weekdays, setWeekDays] = useState(initialState.weekdays);
    const [maxAvailabilityDate, setMaxAvailabilityDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        (async () => {
            if (selectedAllocation && open) {
                try {
                    loader(true);

                    const allocationRanges = await AllocationAndStrategy.GetAllocationDateRanges({
                        allocationIds: [selectedAllocation.Id],
                        calculateAllocatedRooms: true
                    });
                    setAllocationDateRanges(allocationRanges);

                    try {
                        const maxDate = await Availability.GetHotelLastAvailabilityDate({ hotelId: selectedAllocation.HotelId });
                        setMaxAvailabilityDate(maxDate ? new Date(maxDate) : null);
                    } catch (error) {
                        console.error("Error fetching max availability date:", error);
                        setMaxAvailabilityDate(null);
                    }

                    loader(false);

                } catch (error) {
                    onError(error);
                    loader(false);
                }
            }
        })()
    }, [open, selectedAllocation])

    const getMinDate = (arr) => {
        const arrToSort = arr || allocationDateRanges;
        return new Date(arrToSort?.sort((a, b) => new Date(a.StartDate) - new Date(b.StartDate))?.[0]?.StartDate ||
            initialState.startDate);
    }

    const getMaxDate = (arr) => {
        const arrToSort = arr || allocationDateRanges;
        return new Date(arrToSort?.sort((a, b) => new Date(b.EndDate) - new Date(a.EndDate))?.[0]?.EndDate ||
            initialState.endDate);
    }

    const resetState = () => {
        setAllocationDateRanges([]);
        setStartDate(initialState.startDate);
        setEndDate(initialState.endDate);
        setWeekDays(initialState.weekdays);
        setFromFilterDate();
        setToFilterDate();
        setIsDirty(false);
    };

    const getRange = (allocationRange) => ({
        range: [allocationRange.StartDate, allocationRange.EndDate],
        weekdays: allocationRange.Weekdays
    });

    const onDelete = (range) => {
        setAllocationDateRanges((prev) => [...prev.filter(r => r != range)])
        setIsDirty(true);
    }

    const handleAddRange = () => {
        const newRange = {
            TempId: `newRangeTempId-${crypto.randomUUID()}`,
            StartDate: startDate,
            EndDate: endDate,
            Weekdays: getIntWeekdays(weekdays),
            AllocatedRooms: 0,
            AllocationId: selectedAllocation.Id
        };

        const isCollided = checkForCollisionInDateRanges(newRange, allocationDateRanges, { predicate: x => x, getRange })

        if (isCollided) {
            onWarning(message('ErrorOverlappingDates'));
        }
        else {
            setAllocationDateRanges((prev) => [...prev, newRange])
            setIsDirty(true);
        }
    }

    const onSave = async () => {
        try {
            setIsLoading(true);
            loader(true);

            const newAllocationDateRanges = allocationDateRanges.filter(adr => !adr.Id)
            const updatedAllocationRanges = await AllocationAndStrategy.AddAllocationDateRanges(newAllocationDateRanges);

            await RecalculateAvailabilityBySpecification({
                HotelID: selectedAllocation.HotelId,
                StartDate: getMinDate(newAllocationDateRanges),
                EndDate: getMaxDate(newAllocationDateRanges),
                RoomTypeIDs: selectedAllocation.RoomTypeIds,
                AllocationIDs: [selectedAllocation.Id]
            });

            setAllocationDateRanges(updatedAllocationRanges);

            setIsDirty(false);
            onSuccess();
            return true;

        } catch (error) {
            onWarning(error);
            return false;
        } finally {
            setIsLoading(false);
            loader(false);
        }
    };

    const saveAndClose = async () => {
        const ok = await onSave();
        if (ok) {
            resetState();
            onClose?.();
        }
    };

    const handleClose = async () => {
        if (isDirty) {
            if (await confirm({
                content: `${message("WouldYouLikeToContinue")}?`,
                confirmButton: {
                    content: text('Yes'),
                    positive: true,
                },
                cancelButton: {
                    content: text('No'),
                    negative: true,
                },
            })) {
                resetState();
                onClose?.();
            }
        }
        else {
            resetState();
            onClose?.();
        }
    };

    const handleClosePopup = () => {
        resetState();
        onClose();
    };

    const memoDatesForWeekComponent = useMemo(
        () => (startDate && endDate ? [{ startDate, endDate }] : []),
        [startDate, endDate]
    );

    return (
        <Modal
            size='large'
            open={open}
            onClose={handleClose}
            dimmer
            centered
        >
            <Modal.Header>{`${text('AllocationDateRanges')} - ${selectedAllocation?.Name}`}</Modal.Header>

            <Modal.Content
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '60vh',
                }}
            >
                <Form
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    <Form.Group widths={4} >
                        <DatePicker
                            label={text("From")}
                            onChange={(value) => handleStartEndChange([value, endDate], 0, setStartDate, setEndDate)}
                            clearable={false}
                            value={startDate}
                            minDate={moment().startOf("day").toDate()}
                            maxDate={maxAvailabilityDate}
                        />
                        <DatePicker
                            label={text("To")}
                            onChange={(value) => handleStartEndChange([startDate, value], 1, setStartDate, setEndDate)}
                            clearable={false}
                            value={endDate}
                            minDate={moment().startOf("day").toDate()}
                            maxDate={maxAvailabilityDate}
                        />
                        <div className="weekdays-select">
                            <WeekDaysSelect onChange={setWeekDays} dates={memoDatesForWeekComponent} />
                            <div className="add-pricing-button">
                                <Button primary onClick={handleAddRange} disabled={weekdays?.length === 0}>{text("Add")}</Button>
                            </div>
                        </div>
                    </Form.Group>
                    <Form.Group widths={4}>
                        <DatePicker
                            label={`${text("FilterBy")} ${text("FromDate")}`}
                            value={fromFilterDate}
                            onChange={(value) => handleStartEndChange([value, toFilterDate], 0, setFromFilterDate, setToFilterDate)}
                            clearable={false}
                            minDate={getMinDate()}
                            maxDate={getMaxDate()}
                        />
                        <DatePicker
                            label={`${text("FilterBy")} ${text("ToDate")}`}
                            value={toFilterDate}
                            onChange={(value) => handleStartEndChange([fromFilterDate, value], 1, setFromFilterDate, setToFilterDate)}
                            clearable={false}
                            minDate={getMinDate()}
                            maxDate={getMaxDate()}
                        />
                        <div style={{ marginLeft: 5, alignSelf: "flex-end" }}>
                            <Button primary onClick={() => {
                                setFromFilterDate();
                                setToFilterDate();
                            }}>{text("Clear")}</Button>
                        </div>
                        <Form.Field></Form.Field>
                    </Form.Group>
                    <List style={{ overflowY: 'auto' }}>
                        {allocationDateRanges.length > 0
                            ? filterDateRangesByDateRange(allocationDateRanges, fromFilterDate, toFilterDate)
                            .sort((a, b) => moment(a.StartDate).toDate() < moment(b.StartDate).toDate() ? -1 : 1)
                            .map((allocationDateRange) => (
                                <RangeItem
                                    range={allocationDateRange}
                                    key={allocationDateRange.Id || allocationDateRange.TempId}
                                    onDelete={()=> onDelete(allocationDateRange)}
                                />
                            ))
                            : null}
                    </List>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button negative onClick={handleClose} disabled={isLoading}>
                    {text("Close")}
                </Button>
                <Button positive onClick={saveAndClose} loading={isLoading} disabled={isLoading}>
                    {text("Saveandclose")}
                </Button>
                <Button positive onClick={onSave} loading={isLoading} disabled={isLoading}>
                    {text("Save")}
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

const RangeItem = ({ range, onDelete }) => {
    const text = useTextResources();

    const updateRangeValue = (range, value) => {
        range.AllocatedRooms = value;
    }

    return (
        <List.Item>
            <Panel
                color={range.Id ? undefined : "red"}
                header={
                    <List.Description
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div>
                            {`${moment.parseZone(range.StartDate).format(dateFormat)} - ${moment.parseZone(range.EndDate).format(
                                dateFormat
                            )}`}
                            <span style={{ marginLeft: 5 }}><Weekdays selectedWeekdaysInt={range.Weekdays} /></span>
                        </div>
                        <div>
                            {!range.Id && <TrashButton content={"Delete"} onClick={onDelete} floated="right" />}
                        </div>
                    </List.Description>
                }
            >
                <div style={{ display: 'flex' }}>
                    <Form.Field
                        value={range.AllocatedRooms}
                        control={NumericInput}
                        min={0}
                        digits={0}
                        label={text('NumberAllocatedRooms')}
                        buttonPlacement='right'
                        onChange={(value) => { updateRangeValue(range, value) }}
                        disabled={range.Id > 0}
                    />
                </div>
            </Panel>
        </List.Item>
    );
};

RangeItem.propTypes = {
    range: PropTypes.object,
    key: PropTypes.number,
    onDelete: PropTypes.func
};


AllocationDateRangesPopup.propTypes = {
    selectedAllocation: PropTypes.object,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
};
